import { useQuery } from '@apollo/client'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Button, Table } from '@/components_deprecated'
import { VEHICLE_SERVICE_PLANS_QUERY } from '@/modules/vehicles/queries'
import { getVehicleServicePlanInput } from '@/modules/vehicles/util'
import type {
  EditVehicleServicePlanPromptResolve,
  VehicleServicePlanStatus,
} from '@/modules/vehicles/types'
import type {
  VehicleServicePlanNode,
  VehicleServicePlansQuery,
  VehicleServicePlansQueryVariables,
} from '@/modules/vehicles/types.graphql'
import { useMemo } from 'react'
import { format } from '@/utils/date-fns'
import { useConfirm, useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useVehicleServicePlanMutations } from '../mutations.hooks'
import { EditVehicleServicePlanModal } from './edit-vehicle-service-plan-modal'
import { H2 } from '@/components'

interface VehicleServicePlansProps {
  vehicleId: string
}

export const VehicleServicePlans: React.FC<VehicleServicePlansProps> = ({
  vehicleId,
}) => {
  const translations = useTranslate({
    vehicleServices: 'vehicles.vehicle-services',
    add: 'common.add',
    name: 'common.name',
    date: 'common.date',
    endDate: 'common.end-date',
    status: 'common.status',
    workshop: 'vehicles.service.workshop',
    description: 'common.description',
    noServices: 'vehicles.no-vehicle-services',
    statuses: {
      PLANNED: 'vehicles.service.status.planned',
      BOOKED: 'vehicles.service.status.booked',
      PERFORMED: 'vehicles.service.status.performed',
      CANCELLED: 'vehicles.service.status.cancelled',
    },
    prompt: {
      delete: 'vehicles.prompts.delete-vehicle-service',
      deleteConfirmation: [
        'documents.prompts.delete-document-confirmation',
        { name: '' },
      ],
    },
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const onErrorAuto = useOnErrorAuto()
  const { hasPermissions } = usePermissions()
  const canAdd = hasPermissions(
    PERMISSIONS.vehicles.add.vehicleexpiration,
    true
  )
  const canEdit = hasPermissions(
    PERMISSIONS.vehicles.change.vehicleexpiration,
    true
  )
  const canDelete = hasPermissions(
    PERMISSIONS.vehicles.delete.vehicleexpiration,
    true
  )

  const { data, loading } = useQuery<
    VehicleServicePlansQuery,
    VehicleServicePlansQueryVariables
  >(VEHICLE_SERVICE_PLANS_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      vehicle: vehicleId,
      orderBy: 'scheduledStart',
    },
    onError: onErrorAuto(),
  })

  const servicePlans = useMemo(() => {
    return data?.allVehicleServicePlans?.edges?.map(edge => edge?.node) || []
  }, [data])

  const { createServicePlan, patchServicePlan, deleteServicePlan } =
    useVehicleServicePlanMutations()

  async function handleCreate() {
    const { data } =
      await addPrompt<EditVehicleServicePlanPromptResolve | null>(resolve => (
        <EditVehicleServicePlanModal onSubmit={resolve} />
      ))
    if (!data) return

    await createServicePlan({
      variables: {
        input: {
          ...getVehicleServicePlanInput(data.form, data.hasNotification),
          vehicle: vehicleId,
        },
      },
    })
  }

  async function handleEdit(servicePlan: VehicleServicePlanNode) {
    const { data } =
      await addPrompt<EditVehicleServicePlanPromptResolve | null>(resolve => (
        <EditVehicleServicePlanModal
          servicePlan={servicePlan}
          onSubmit={resolve}
        />
      ))
    if (!data) return

    await patchServicePlan({
      variables: {
        id: servicePlan.id,
        input: getVehicleServicePlanInput(data.form, data.hasNotification),
      },
    })
  }

  async function handleDelete(servicePlan: VehicleServicePlanNode) {
    const start = format(new Date(servicePlan.scheduledStart), 'PP')
    const { data: answer } = await confirm(
      translations.prompt.deleteConfirmation({
        name: `${start} - ${servicePlan.name}`,
      }),
      translations.prompt.delete
    )
    if (!answer) return

    await deleteServicePlan({ variables: { id: servicePlan.id } })
  }

  const statusLabel = (status: string) =>
    translations.statuses[status as VehicleServicePlanStatus] ?? status

  return (
    <div className="mt-4">
      <div className="my-4 flex flex-col items-stretch justify-between gap-4 md:flex-row md:items-center md:gap-0">
        <H2>{translations.vehicleServices}</H2>
        {canAdd && (
          <Button
            iconLeftProps={{ icon: 'plus', size: '1rem', type: 'solid' }}
            height="3rem"
            onClick={handleCreate}
          >
            {translations.add}
          </Button>
        )}
      </div>
      <Table
        loading={loading}
        noShadow
        noData={!servicePlans.length}
        noDataText={translations.noServices}
      >
        <thead>
          <tr>
            <th>{translations.date}</th>
            <th>{translations.endDate}</th>
            <th>{translations.name}</th>
            <th>{translations.status}</th>
            <th>{translations.workshop}</th>
            {canEdit && <th></th>}
            {canDelete && <th></th>}
          </tr>
        </thead>
        <tbody>
          {servicePlans.map(servicePlan => {
            const start = servicePlan.scheduledStart
              ? format(new Date(servicePlan.scheduledStart), 'PPp')
              : '-'
            const end = servicePlan.scheduledEnd
              ? format(new Date(servicePlan.scheduledEnd), 'PPp')
              : '-'
            const cancelled = servicePlan.status === 'CANCELLED'
            return (
              <tr
                key={servicePlan.id}
                className={cancelled ? 'text-gray-400 line-through' : undefined}
              >
                <td>{start}</td>
                <td>{end}</td>
                <td className="wrap">{servicePlan.name}</td>
                <td>{statusLabel(servicePlan.status)}</td>
                <td className="wrap">{servicePlan.workshopName || '-'}</td>
                {canEdit && (
                  <td width="1px" className="center">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEdit(servicePlan)}
                    />
                  </td>
                )}
                {canDelete && (
                  <td>
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleDelete(servicePlan)}
                    />
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </Table>
    </div>
  )
}
